import type { Card } from "@amzn/ads-blink-client";
import type {
    Content,
    ContentUser,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import React from "react";
import { useStore } from "zustand";

import { MarketingNarrativeDetailsType } from "../../../../hooks/marketingContent/getMarketingNarrativeDetails";
import {
    useMarketingNarrativeDetails,
    useUnifiedMarketingNarrativeDetails,
} from "../../../../hooks/marketingContent/useMarketingNarrativeDetails";
import { useContentCardShown } from "../../../../hooks/misc/useContentCardShown";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import type { CardDetail } from "../../../../types/card";
import type { KeyValue } from "../../../../types/common";
import { insightTypeLabelToValue } from "../../../../types/filters/unified";
import { CardLayout } from "../insightDetails/CardLayout";
import { CardLayoutV3 } from "../insightDetails/CardLayoutV3";

import { MarketingNarrativeCardAction } from "./MarketingNarrativeCardAction";

const CONTENT_TYPE_FIELD = "Content Type";

interface MarketingNarrativeG4SDetails {
    responseId: string;
    summary?: string;
}

export interface MarketingNarrativeCardProps {
    content: Content;
    contentUser: ContentUser[];
    index?: number;
    unifiedCard?: Card;
    g4sDetails?: MarketingNarrativeG4SDetails;
    additionalCardDetails?: CardDetail[];
    isExploreWithAI?: boolean;
}

export const MarketingNarrativeCard: React.FunctionComponent<
    MarketingNarrativeCardProps
> = ({
    content,
    contentUser,
    index,
    unifiedCard,
    g4sDetails,
    additionalCardDetails = [],
    isExploreWithAI = false,
}) => {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    // Always call both hooks unconditionally
    const standardDetails = useMarketingNarrativeDetails(
        content,
        contentUser,
        MarketingNarrativeDetailsType.CARD,
    );

    const unifiedDetails = useUnifiedMarketingNarrativeDetails(
        content,
        contentUser,
        MarketingNarrativeDetailsType.CARD,
        unifiedCard,
    );

    // Then conditionally use the results
    const details = [
        ...(useUnifiedSearch ? unifiedDetails : standardDetails),
        ...additionalCardDetails,
    ];

    useContentCardShown({
        insightType: InsightType.MARKETING,
        identifier: content.contentId,
    });

    const identifier = content.contentId;
    const cardHeader = content.contentTitle;
    const insightType = InsightType.MARKETING;
    const lastModifiedDate = content.lastModifiedDate;
    const additionalInfo = {
        downloads: content.totalDownloads,
    };

    return isExploreWithAI ? (
        <CardLayout
            dataTestId={content.contentId!}
            description={content.contentTitle!}
            details={
                g4sDetails
                    ? details.filter(
                          (item) => item.label !== CONTENT_TYPE_FIELD,
                      )
                    : details
            }
            actions={
                <MarketingNarrativeCardAction
                    identifier={content.contentId!}
                    index={index}
                    g4sResponseId={g4sDetails?.responseId}
                />
            }
            summary={g4sDetails?.summary}
        />
    ) : (
        <CardLayoutV3
            cardHeader={cardHeader!}
            insightType={insightType}
            insightTypeName={insightTypeLabelToValue[insightType] ?? undefined}
            additionalInfo={additionalInfo as KeyValue}
            lastModifiedDate={lastModifiedDate}
            actions={
                <MarketingNarrativeCardAction
                    identifier={content.contentId!}
                    index={index}
                    g4sResponseId={g4sDetails?.responseId}
                />
            }
            dataTestId={identifier ?? ""}
            key={identifier}
        />
    );
};
